import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { TriStateCheckbox } from "primereact/tristatecheckbox";
import {
  ProtocolGradeResponse,
  ProtocolResponse,
  ProtocolStudentGradeComponentResponse,
  ProtocolGradeRequest,
} from "../../models/dtos";
import { protocolsApi } from "../../services/protocols/protocolsApi";
import Error from "../../components/common/Error";
import { Skeleton } from "primereact/skeleton";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { useRef, useState, useEffect, useMemo, memo } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { studentGradeTypesApi } from "../../services/nomenclatures/studentGradeTypesApi";
import CustomDatepicker from "../../components/forms/CustomDatepicker";
import { Formik } from "formik";
import { formatGrade } from "../../helpers/gradeHelpers";

interface ProtocolGradesListProps {
  protocol: ProtocolResponse;
}

interface EditableProtocolGrade extends ProtocolGradeResponse {
  gradeInput: string;
  thesisTitleInput: string;
}

// Memoized grade cell component
const GradeCell = ({
  rowData,
  options,
  editMode,
  handleGradeKeyDown,
  handleGradeInputChange,
  handleGradeInputBlur,
}: {
  rowData: EditableProtocolGrade;
  options: any;
  editMode: boolean;
  handleGradeKeyDown: (e: React.KeyboardEvent<HTMLInputElement>, options: any, rowData: EditableProtocolGrade) => void;
  handleGradeInputChange: (studentId: number, value: string) => void;
  handleGradeInputBlur: (studentId: number) => void;
}) => {
  return editMode ? (
    <InputText
      value={rowData.gradeInput}
      onClick={(e) => (e.target as HTMLInputElement).select()}
      onKeyDown={(e) => handleGradeKeyDown(e, options, rowData)}
      onChange={(e) => handleGradeInputChange(rowData.studentId, e.target.value)}
      onBlur={() => handleGradeInputBlur(rowData.studentId)}
      className="w-full protocol-grade-input"
    />
  ) : (
    formatGrade(rowData.grade)
  );
};

const ProtocolGradesList = ({ protocol }: ProtocolGradesListProps) => {
  const toast = useRef<Toast>(null);
  const [editMode, setEditMode] = useState(false);
  const [editingGrades, setEditingGrades] = useState<EditableProtocolGrade[]>([]);
  const [originalGrades, setOriginalGrades] = useState<ProtocolGradeResponse[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: grades,
    error: gradesError,
    isLoading: gradesLoading,
    refetch: refetchGrades,
  } = protocolsApi.useGetProtocolGradesQuery(protocol.protocolId);

  const { data: studentGradeTypes } = studentGradeTypesApi.useGetStudentGradeTypesQuery();

  const gradeTypeOptions = useMemo(
    () => studentGradeTypes?.data?.map((type) => ({ label: type.name, value: type.studentGradeTypeId })) ?? [],
    [studentGradeTypes?.data],
  );

  const [removeStudent] = protocolsApi.useRemoveProtocolStudentMutation();

  const [updateStudentGrade] = protocolsApi.useAdminUpdateStudentGradeMutation();

  useEffect(() => {
    if (grades?.data) {
      const editable = grades.data.map((grade) => ({
        ...grade,
        gradeInput: formatGrade(grade.grade),
        thesisTitleInput: grade.thesisTitle || "",
      }));
      setEditingGrades(editable);
      setOriginalGrades(structuredClone(grades.data));
    }
  }, [grades?.data]);

  const handleDelete = (studentId: number) => {
    confirmDialog({
      message: "Сигурни ли сте, че искате да премахнете студента от протокола?",
      header: "Потвърждение",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        removeStudent({ protocolId: protocol.protocolId, studentId })
          .unwrap()
          .then((result) => {
            if (result && "data" in result) {
              if (result.data === true) {
                toast.current?.show({
                  severity: "success",
                  summary: "Успешно",
                  detail: "Студентът беше премахнат от протокола",
                });
              } else {
                toast.current?.show({
                  severity: "error",
                  summary: "Грешка",
                  detail: result.message,
                });
              }
            }
          });
      },
    });
  };

  const handleFieldEdit = (
    studentId: number,
    updatedFields: Partial<ProtocolGradeResponse & { gradeInput?: string; thesisTitleInput?: string }>,
  ) => {
    setEditingGrades((prev) => prev.map((g) => (g.studentId === studentId ? { ...g, ...updatedFields } : g)));
  };

  const onMulticomponentGradeEdit = (rowData: EditableProtocolGrade, subjectId: number, newValue: string) => {
    let updatedComponents = [...rowData.studentGradeComponents];
    const existingComponent = updatedComponents.find((sgc) => sgc.subjectId === subjectId);

    if (existingComponent) {
      updatedComponents = updatedComponents.map((sgc) =>
        sgc.subjectId === subjectId ? { ...sgc, grade: parseInt(newValue) } : sgc,
      );
    } else {
      updatedComponents.push({
        studentGradeId: rowData.studentGradeId,
        subjectId: subjectId,
        grade: parseInt(newValue),
      });
    }

    handleFieldEdit(rowData.studentId, { studentGradeComponents: updatedComponents });
  };

  const isValidGrade = (value: string): boolean => {
    const parsedValue = parseFloat(value);
    const validWholeGrades = [0, 2, 3, 4, 5, 6];
    const validDecimalGrades = [3.25, 3.5, 3.75, 4.25, 4.5, 4.75, 5.25, 5.5, 5.75];
    return validWholeGrades.includes(parsedValue) || validDecimalGrades.includes(parsedValue);
  };

  const handleGradeInputChange = (studentId: number, value: string) => {
    if (!/^\d*\.?\d*$/.test(value)) return;
    setEditingGrades((prev) => prev.map((g) => (g.studentId === studentId ? { ...g, gradeInput: value } : g)));
  };

  const handleGradeInputBlur = (studentId: number) => {
    const item = editingGrades.find((g) => g.studentId === studentId);
    if (!item) return;
    const { gradeInput } = item;
    if (isValidGrade(gradeInput)) {
      handleFieldEdit(studentId, { grade: parseFloat(gradeInput) || 0, gradeInput: gradeInput });
    } else {
      setEditingGrades((prev) =>
        prev.map((g) => (g.studentId === studentId ? { ...g, gradeInput: formatGrade(g.grade) } : g)),
      );
    }
  };

  const handleThesisTitleInputChange = (studentId: number, value: string) => {
    setEditingGrades((prev) => prev.map((g) => (g.studentId === studentId ? { ...g, thesisTitleInput: value } : g)));
  };

  const handleThesisTitleInputBlur = (studentId: number) => {
    const item = editingGrades.find((g) => g.studentId === studentId);
    if (item) {
      handleFieldEdit(studentId, { thesisTitle: item.thesisTitleInput, thesisTitleInput: item.thesisTitleInput });
    }
  };

  const handleAttestationEdit = (rowData: ProtocolGradeResponse, newValue: boolean | null) => {
    handleFieldEdit(rowData.studentId, { attestation: newValue });
  };

  const handleGradeTypeEdit = (rowData: ProtocolGradeResponse, newValue: string) => {
    handleFieldEdit(rowData.studentId, { studentGradeTypeId: parseInt(newValue) });
  };

  const handleUpdateDateEdit = (rowData: ProtocolGradeResponse, newValue: Date | null) => {
    handleFieldEdit(rowData.studentId, { updateDate: newValue });
  };

  const handleGradeKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    options: any,
    rowData: ProtocolGradeResponse,
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleGradeInputBlur(rowData.studentId);
      const inputs = document.querySelectorAll(".protocol-grade-input") as NodeListOf<HTMLInputElement>;
      const nextInput = inputs[options.rowIndex + 1];
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    }
  };

  const hasChanges = () => {
    for (let i = 0; i < editingGrades.length; i++) {
      const current = editingGrades[i];
      const original = originalGrades.find((g) => g.studentId === current.studentId);

      if (!original) continue;

      if (
        current.grade !== original.grade ||
        current.studentGradeTypeId !== original.studentGradeTypeId ||
        current.attestation !== original.attestation ||
        current.thesisTitle !== original.thesisTitle ||
        JSON.stringify(current.studentGradeComponents) !== JSON.stringify(original.studentGradeComponents) ||
        current.updateDate?.toString() !== original.updateDate?.toString()
      ) {
        return true;
      }
    }
    return false;
  };

  const saveAllChanges = async () => {
    if (!hasChanges()) {
      setEditMode(false);
      return;
    }

    setIsSubmitting(true);

    try {
      const changedGrades = editingGrades.filter((current) => {
        const original = originalGrades.find((g) => g.studentId === current.studentId);
        if (!original) return false;

        return (
          current.grade !== original.grade ||
          current.studentGradeTypeId !== original.studentGradeTypeId ||
          current.attestation !== original.attestation ||
          current.thesisTitle !== original.thesisTitle ||
          JSON.stringify(current.studentGradeComponents) !== JSON.stringify(original.studentGradeComponents) ||
          current.updateDate?.toString() !== original.updateDate?.toString()
        );
      });

      // Process in sequential order
      for (const grade of changedGrades) {
        const gradeRequest: ProtocolGradeRequest = {
          studentGradeId: grade.studentGradeId,
          studentId: grade.studentId,
          protocolId: grade.protocolId,
          grade: grade.grade,
          studentGradeTypeId: grade.studentGradeTypeId,
          attestation: grade.attestation,
          studentGradeComponents: grade.studentGradeComponents,
          updateDate: grade.updateDate || null,
          thesisTitle: grade.thesisTitle || null,
        };

        await updateStudentGrade(gradeRequest).unwrap();
      }

      toast.current?.show({
        severity: "success",
        summary: "Успешно",
        detail: `${changedGrades.length} ${changedGrades.length === 1 ? "оценка беше" : "оценки бяха"} успешно обновени`,
      });

      refetchGrades();
      setEditMode(false);
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Грешка",
        detail: "Възникна грешка при запазването на оценките",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (gradesLoading) return <Skeleton className="w-full h-20rem" />;
  if (gradesError) return <Error error={gradesError} />;

  return (
    <>
      <Toast ref={toast} />
      <Card className="shadow-4">
        <div className="flex justify-content-between align-items-center">
          <h3>Студенти</h3>
          {editMode ? (
            <div className="flex gap-2">
              <Button
                icon="pi pi-times"
                rounded
                severity="secondary"
                onClick={() => {
                  // Reset to original data
                  if (grades?.data) {
                    const editable = grades.data.map((grade) => ({
                      ...grade,
                      gradeInput: formatGrade(grade.grade),
                      thesisTitleInput: grade.thesisTitle || "",
                    }));
                    setEditingGrades(editable);
                  }
                  setEditMode(false);
                }}
                disabled={isSubmitting}
              />
              <Button
                icon="pi pi-check"
                rounded
                severity="success"
                onClick={saveAllChanges}
                loading={isSubmitting}
                disabled={!hasChanges()}
              />
            </div>
          ) : (
            <Button icon="pi pi-pencil" rounded onClick={() => setEditMode(true)} />
          )}
        </div>
        {protocol.examTypeId === 13 || protocol.examTypeId === 17 ? (
          <DataTable key={editMode ? 'edit' : 'view'} value={editingGrades} stripedRows showGridlines>
            <Column
              header="ФН"
              sortable
              style={{ width: "140px" }}
              body={(rowData) => (
                <div className="flex align-items-center">
                  <span>{rowData.facultyNumber}</span>
                  <a href={`/students/${rowData.studentId}`} target="_blank" rel="noopener noreferrer" className="ml-2">
                    <Button label="" icon="pi pi-external-link" size="small" severity="secondary" />
                  </a>
                </div>
              )}
            />
            <Column field="studentName" header="Име" sortable />
            <Column field="groupName" header="Група" sortable />
            {protocol.examTypeId === 17 && (
              <Column
                field="thesisTitle"
                header="Заглавие на дипломна работа"
                style={{ width: "300px" }}
                body={(rowData) =>
                  editMode ? (
                    <InputText
                      value={(rowData as EditableProtocolGrade).thesisTitleInput}
                      onChange={(e) => handleThesisTitleInputChange(rowData.studentId, e.target.value)}
                      onBlur={() => handleThesisTitleInputBlur(rowData.studentId)}
                      className="w-full"
                    />
                  ) : (
                    rowData.thesisTitle || ""
                  )
                }
              />
            )}
            {protocol.childSubjects.map((cs) => (
              <Column
                field={cs.subjectId.toString()}
                header={cs.name}
                style={{ width: "80px" }}
                body={(rowData, options) =>
                  editMode ? (
                    <InputText
                      value={rowData.studentGradeComponents
                        .find((sgc: ProtocolStudentGradeComponentResponse) => sgc.subjectId === cs.subjectId)
                        ?.grade.toString()}
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                      onKeyDown={(e) => handleGradeKeyDown(e, options, rowData)}
                      onChange={(e) => onMulticomponentGradeEdit(rowData, cs.subjectId, e.target.value)}
                      className="w-full protocol-grade-input"
                    />
                  ) : (
                    rowData.studentGradeComponents
                      .find((sgc: ProtocolStudentGradeComponentResponse) => sgc.subjectId === cs.subjectId)
                      ?.grade.toString() || ""
                  )
                }
              />
            ))}
            <Column
              field="grade"
              header="Оценка"
              style={{ width: "80px" }}
              body={(rowData, options) => (
                <GradeCell
                  rowData={rowData as EditableProtocolGrade}
                  options={options}
                  editMode={editMode}
                  handleGradeKeyDown={handleGradeKeyDown}
                  handleGradeInputChange={handleGradeInputChange}
                  handleGradeInputBlur={handleGradeInputBlur}
                />
              )}
            />
            <Column
              field="studentGradeTypeName"
              header="Тип оценка"
              style={{ width: "80px" }}
              body={(rowData) =>
                editMode ? (
                  <Dropdown
                    value={rowData.studentGradeTypeId}
                    options={gradeTypeOptions}
                    onChange={(e) => handleGradeTypeEdit(rowData, e.value)}
                    className="w-full"
                  />
                ) : (
                  rowData.studentGradeTypeName
                )
              }
            />
            <Column
              field="updateDate"
              header="Дата"
              style={{ width: "200px" }}
              body={(rowData) =>
                editMode ? (
                  <Formik
                    initialValues={{ updateDate: rowData.updateDate ? new Date(rowData.updateDate) : null }}
                    onSubmit={() => {}}
                  >
                    <CustomDatepicker
                      id="updateDate"
                      label=""
                      showIcon={false}
                      onChange={(e: any) => handleUpdateDateEdit(rowData, e.value)}
                      className="w-full"
                    />
                  </Formik>
                ) : rowData.updateDate ? (
                  new Date(rowData.updateDate).toLocaleDateString("bg-BG")
                ) : (
                  ""
                )
              }
            />
            <Column
              field="modifiedByUsername"
              header="Въведено от"
              style={{ width: "120px" }}
              body={(rowData) => (rowData.modifiedByUsername ? rowData.modifiedByUsername : "")}
            />
            <Column
              body={(rowData) => (
                <Button
                  icon="pi pi-trash"
                  severity="danger"
                  size="small"
                  onClick={() => handleDelete(rowData.studentId)}
                />
              )}
              style={{ width: "80px" }}
            />
          </DataTable>
        ) : (
          <DataTable key={editMode ? 'edit' : 'view'} value={editingGrades} stripedRows showGridlines>
            <Column
              header="ФН"
              style={{ width: "140px" }}
              sortable
              body={(rowData) => (
                <div className="flex align-items-center">
                  <span>{rowData.facultyNumber}</span>
                  <a href={`/students/${rowData.studentId}`} target="_blank" rel="noopener noreferrer" className="ml-2">
                    <Button label="" icon="pi pi-external-link" size="small" severity="secondary" />
                  </a>
                </div>
              )}
            />
            <Column field="studentName" header="Име" sortable />
            <Column field="groupName" header="Група" sortable />

            {protocol.examTypeId !== 14 && (
              <Column
                field="grade"
                header="Оценка"
                style={{ width: "80px" }}
                body={(rowData, options) => (
                  <GradeCell
                    rowData={rowData as EditableProtocolGrade}
                    options={options}
                    editMode={editMode}
                    handleGradeKeyDown={handleGradeKeyDown}
                    handleGradeInputChange={handleGradeInputChange}
                    handleGradeInputBlur={handleGradeInputBlur}
                  />
                )}
              />
            )}
            {protocol.examTypeId === 14 && (
              <Column
                field="attestation"
                header="Заверка"
                style={{ width: "140px" }}
                body={(rowData) => (
                  <div className="flex flex-row gap-2 align-items-center">
                    {editMode && (
                      <TriStateCheckbox
                        value={rowData.attestation}
                        onChange={(e) => handleAttestationEdit(rowData, e.value as boolean | null)}
                      />
                    )}
                    {rowData.attestation === true ? "Да" : rowData.attestation === false ? "Не" : "Непопълнено"}
                  </div>
                )}
              />
            )}
            <Column
              field="studentGradeTypeName"
              header="Тип оценка"
              style={{ width: "80px" }}
              body={(rowData) =>
                editMode ? (
                  <Dropdown
                    value={rowData.studentGradeTypeId}
                    options={gradeTypeOptions}
                    onChange={(e) => handleGradeTypeEdit(rowData, e.value)}
                    className="w-full"
                  />
                ) : (
                  rowData.studentGradeTypeName
                )
              }
            />
            <Column
              field="updateDate"
              header="Дата"
              style={{ width: "200px" }}
              body={(rowData) =>
                editMode ? (
                  <Formik
                    initialValues={{ updateDate: rowData.updateDate ? new Date(rowData.updateDate) : null }}
                    onSubmit={() => {}}
                  >
                    <CustomDatepicker
                      id="updateDate"
                      label=""
                      showIcon={false}
                      onChange={(e: any) => handleUpdateDateEdit(rowData, e.value)}
                      className="w-full"
                    />
                  </Formik>
                ) : rowData.updateDate ? (
                  new Date(rowData.updateDate).toLocaleDateString("bg-BG")
                ) : (
                  ""
                )
              }
            />
            <Column
              field="modifiedByUsername"
              header="Въведено от"
              style={{ width: "120px" }}
              body={(rowData) => (rowData.modifiedByUsername ? rowData.modifiedByUsername : "")}
            />
            <Column
              body={(rowData) => (
                <Button
                  icon="pi pi-trash"
                  severity="danger"
                  size="small"
                  onClick={() => handleDelete(rowData.studentId)}
                />
              )}
              style={{ width: "80px" }}
            />
          </DataTable>
        )}

        <ConfirmDialog />
      </Card>
    </>
  );
};

export default ProtocolGradesList;
